import React, { useEffect, useRef, useState } from "react";

/**
 * Mascote Evolzap animado.
 *
 * Loop simples (~7s por ciclo, repete infinitamente enquanto visivel):
 *   0.0s  cruzado (olhos abertos)          — pose de descanso
 *   3.5s  cruzadoBlink                     — 1a piscada
 *   3.68s cruzado                          — abre olhos
 *   3.93s cruzadoBlink                     — 2a piscada
 *   4.11s cruzado                          — segura ate fechar
 *   7.0s  volta pro comeco
 *
 * prefers-reduced-motion: pula piscadas, mostra so o frame estatico.
 * Pausa: quando fora da viewport, o loop para (economia de CPU).
 */

const FRAMES = {
  cruzado: "/robo-01-cruzado.webp",
  cruzadoBlink: "/robo-02-cruzado-blink.webp",
};

const SEQUENCIA_LOOP = [
  { frame: "cruzado", duracao: 3500 },
  { frame: "cruzadoBlink", duracao: 180 },
  { frame: "cruzado", duracao: 250 },
  { frame: "cruzadoBlink", duracao: 180 },
  { frame: "cruzado", duracao: 2890 },
];

const FRAME_ESTATICO = "cruzado";

export default function RoboAnimado({ className = "" }) {
  const [frame, setFrame] = useState(FRAME_ESTATICO);
  const [animar, setAnimar] = useState(true);
  const [visivel, setVisivel] = useState(false);
  const containerRef = useRef(null);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    Object.values(FRAMES).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      setAnimar(false);
      setFrame(FRAME_ESTATICO);
    }
  }, []);

  useEffect(() => {
    if (!animar) return;
    if (!containerRef.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisivel(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setVisivel(entry.isIntersecting),
      { threshold: 0.2 }
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, [animar]);

  useEffect(() => {
    if (!animar) return;
    if (!visivel) return;

    let cancelado = false;

    const rodarCiclo = () => {
      if (cancelado) return;
      let acumulado = 0;
      SEQUENCIA_LOOP.forEach((passo, i) => {
        if (i === 0) {
          setFrame(passo.frame);
          acumulado += passo.duracao;
          return;
        }
        const t = setTimeout(() => {
          if (cancelado) return;
          setFrame(passo.frame);
        }, acumulado);
        timeoutsRef.current.push(t);
        acumulado += passo.duracao;
      });

      const tProximo = setTimeout(() => {
        if (!cancelado) rodarCiclo();
      }, acumulado);
      timeoutsRef.current.push(tProximo);
    };

    rodarCiclo();

    return () => {
      cancelado = true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [animar, visivel]);

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center justify-center ${className}`}
      data-testid="robo-animado"
    >
      {/* Glow verde por tras do mascote */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="w-[70%] h-[70%] bg-green-500/20 rounded-full blur-3xl" />
      </div>

      <img
        src={FRAMES[frame]}
        alt="Mascote Evolzap"
        className={`relative w-full h-auto max-w-md mx-auto ${animar ? "animate-float" : ""}`}
        draggable={false}
      />
    </div>
  );
}
